import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiSave, FiCamera } from 'react-icons/fi';
import api from '../config/api';
import LoadingButton from './LoadingButton';

const PROVIDERS = [
  { id: 'custom', label: 'Custom HTTP endpoint' },
  { id: 'google_vision', label: 'Google Cloud Vision (future)' },
  { id: 'aws_textract', label: 'AWS Textract (future)' },
  { id: 'azure_di', label: 'Azure Document Intelligence (future)' },
  { id: 'openai', label: 'OpenAI / vision model (future)' },
];

const emptyForm = {
  enabled: false,
  provider: 'custom',
  api_url: '',
  api_key: '',
  auth_header: 'Authorization',
  auth_scheme: 'Bearer',
  timeout_ms: 30000,
  request_mode: 'multipart',
  file_field: 'file',
};

const OcrSettings = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [message, setMessage] = useState('');

  const { data: settings, isLoading } = useQuery(['ocr-config'], () =>
    api.get('/settings/ocr').then((r) => r.data)
  );

  useEffect(() => {
    if (!settings) return;
    setForm({
      enabled: Boolean(settings.enabled),
      provider: settings.provider || 'custom',
      api_url: settings.api_url || '',
      api_key: '',
      auth_header: settings.auth_header || 'Authorization',
      auth_scheme: settings.auth_scheme || 'Bearer',
      timeout_ms: settings.timeout_ms || 30000,
      request_mode: settings.request_mode || 'multipart',
      file_field: settings.file_field || 'file',
    });
  }, [settings]);

  const saveMutation = useMutation((body) => api.put('/settings/ocr', body), {
    onSuccess: (res) => {
      queryClient.setQueryData(['ocr-config'], res.data);
      queryClient.invalidateQueries(['buying-ocr-status']);
      setForm((f) => ({ ...f, api_key: '' }));
      setMessage(
        res.data.configured
          ? 'OCR saved. Documents can be scanned to prefill details.'
          : 'OCR saved. Leave it disabled (or incomplete) to keep manual entry.'
      );
    },
    onError: (e) => setMessage(e.response?.data?.error || e.message || 'Save failed'),
  });

  const handleSave = (e) => {
    e.preventDefault();
    setMessage('');
    const body = {
      enabled: form.enabled,
      provider: form.provider,
      api_url: form.api_url.trim(),
      auth_header: form.auth_header.trim() || 'Authorization',
      auth_scheme: form.auth_scheme,
      timeout_ms: Number(form.timeout_ms) || 30000,
      request_mode: form.request_mode,
      file_field: form.file_field.trim() || 'file',
    };
    if (form.api_key.trim()) body.api_key = form.api_key.trim();
    saveMutation.mutate(body);
  };

  if (isLoading) return <div className="loading">Loading OCR settings…</div>;

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h2>
          <FiCamera /> OCR
        </h2>
        <p>
          Optional shared OCR service. When enabled, Buying bills/quotations and candidate resume
          uploads are sent to OCR as soon as a file is chosen. The OCR text is then mapped by your
          Document OCR autofill AI model onto the app's form fields. Until an OCR API is set, users
          enter details manually.
        </p>
      </div>

      <form className="settings-form" onSubmit={handleSave}>
        <label className="form-checkbox-label">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
          />
          Enable OCR scanning
        </label>

        <div className="form-group">
          <label htmlFor="ocr-provider">Provider</label>
          <select
            id="ocr-provider"
            value={form.provider}
            onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))}
          >
            {PROVIDERS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
          <p className="form-hint">
            Native Google / AWS / Azure / OpenAI adapters are future-scope. Point a custom HTTP
            endpoint at your OCR API today (multipart file or JSON base64).
          </p>
        </div>

        <div className="form-group">
          <label htmlFor="ocr-url">API URL</label>
          <input
            id="ocr-url"
            type="url"
            autoComplete="off"
            value={form.api_url}
            onChange={(e) => setForm((f) => ({ ...f, api_url: e.target.value }))}
            placeholder="https://ocr.example.com/extract"
          />
        </div>

        <div className="form-group">
          <label htmlFor="ocr-key">API key</label>
          <input
            id="ocr-key"
            type="password"
            autoComplete="new-password"
            value={form.api_key}
            onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
            placeholder={
              settings?.api_key_set
                ? `Saved (${settings.api_key_preview || '••••'}) — leave blank to keep`
                : 'Provider API key'
            }
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="ocr-scheme">Auth scheme</label>
            <select
              id="ocr-scheme"
              value={form.auth_scheme}
              onChange={(e) => setForm((f) => ({ ...f, auth_scheme: e.target.value }))}
            >
              <option value="Bearer">Bearer</option>
              <option value="ApiKey">Raw header value</option>
              <option value="Header">Custom header</option>
              <option value="none">None</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="ocr-header">Auth header</label>
            <input
              id="ocr-header"
              value={form.auth_header}
              onChange={(e) => setForm((f) => ({ ...f, auth_header: e.target.value }))}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="ocr-mode">Request body</label>
            <select
              id="ocr-mode"
              value={form.request_mode}
              onChange={(e) => setForm((f) => ({ ...f, request_mode: e.target.value }))}
            >
              <option value="multipart">Multipart file</option>
              <option value="json_base64">JSON + base64</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="ocr-field">File field name</label>
            <input
              id="ocr-field"
              value={form.file_field}
              onChange={(e) => setForm((f) => ({ ...f, file_field: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label htmlFor="ocr-timeout">Timeout (ms)</label>
            <input
              id="ocr-timeout"
              type="number"
              min="3000"
              max="120000"
              value={form.timeout_ms}
              onChange={(e) => setForm((f) => ({ ...f, timeout_ms: e.target.value }))}
            />
          </div>
        </div>

        <p className="form-hint">
          Response mapping depends on the document type. Vendor bills/quotations use fields such as
          vendor_name, invoice_number, date, total, tax, and lines[]. Resume extraction will use the
          same API with a different mapping later.
        </p>

        {message ? <p className="settings-message">{message}</p> : null}

        <div className="form-actions">
          <LoadingButton
            type="submit"
            className="btn btn-primary"
            icon={FiSave}
            loading={saveMutation.isLoading}
            loadingLabel="Saving..."
          >
            Save OCR settings
          </LoadingButton>
        </div>
      </form>
    </div>
  );
};

export default OcrSettings;
